import {html, svg, type HTMLTemplateResult, type SVGTemplateResult} from "lit";
import {formatTime, pad2} from "../../../../domain/gimmicks/stopwatch.state";
import type {SkinInput} from "./skin-input";

/** The 60 ticks of the dial, every fifth strong — built once, the dial never changes. */
const TICKS: readonly SVGTemplateResult[] = Array.from({length: 60}, (_, i) => {
    const strong = i % 5 === 0;
    return svg`<line class=${strong ? 'stopwatch-chrono__tick stopwatch-chrono__tick--strong' : 'stopwatch-chrono__tick'}
                     x1="100" y1="6" x2="100" y2=${strong ? 20 : 13} transform=${`rotate(${i * 6} 100 100)`}></line>`;
});

/**
 * "Chronograph": a light classic dial, 60 ticks, a gliding seconds hand (one full turn per minute, moved on
 * every frame), a small sub-dial counting 60 minutes, the time digitally below the centre. Hands and digits
 * are dark; the accent marks only the hand's tip and the hub (accent on white would be ~1.7:1, plan review).
 * The hundredths sit in their own `tspan`, so a small tile can drop them (ralphy.css); font sizes and anchoring
 * come from there too, the `--long` modifier from one hour on.
 */
export function renderChronoSkin({ms, parts}: SkinInput): HTMLTemplateResult {
    const secondsAngle = ((ms % 60000) / 60000) * 360;
    const minutesAngle = ((ms % 3600000) / 3600000) * 360;

    return html`
        <svg class="stopwatch-chrono" viewBox="0 0 200 200" aria-hidden="true">
            ${svg`
                <circle class="stopwatch-chrono__face" cx="100" cy="100" r="98"></circle>
                ${TICKS}
                <circle class="stopwatch-chrono__subdial" cx="100" cy="62" r="20"></circle>
                <line class="stopwatch-chrono__subhand" x1="100" y1="62" x2="100" y2="46"
                      transform=${`rotate(${minutesAngle.toFixed(2)} 100 62)`}></line>
                <text class=${parts.h > 0 ? 'stopwatch-chrono__time stopwatch-chrono__time--long' : 'stopwatch-chrono__time'}
                      x="100" y="146">${formatTime(parts, false)}<tspan class="stopwatch__hundredths">,${pad2(parts.cs)}</tspan></text>
                <g class="stopwatch-chrono__hand" transform=${`rotate(${secondsAngle.toFixed(2)} 100 100)`}>
                    <line class="stopwatch-chrono__hand-body" x1="100" y1="118" x2="100" y2="22"></line>
                    <line class="stopwatch-chrono__hand-tip" x1="100" y1="36" x2="100" y2="14"></line>
                </g>
                <circle class="stopwatch-chrono__hub" cx="100" cy="100" r="5"></circle>
            `}
        </svg>
    `;
}
